import React, { useState, useEffect } from 'react';
import { Star, User, ChevronDown, ChevronUp } from 'lucide-react';

const BookReviews = ({ age }) => {
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState({});
  const [showForm, setShowForm] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  const [formData, setFormData] = useState({
    rating: 5,
    title: '',
    content: '',
    reviewer_name: '',
    isAnonymous: false,
  });

  useEffect(() => {
    fetchReviews();
  }, [age]);

  const fetchReviews = async () => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const [reviewsRes, statsRes] = await Promise.all([
        fetch(`${API_URL}/api/reviews?age=${age}`),
        fetch(`${API_URL}/api/reviews/stats`),
      ]);
      
      if (reviewsRes.ok) setReviews(await reviewsRes.json());
      if (statsRes.ok) setStats(await statsRes.json());
    } catch (error) {
      console.error('Error fetching reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/reviews`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          age: parseInt(age),
          rating: formData.rating,
          title: formData.title,
          content: formData.content,
          reviewer_name: formData.isAnonymous ? null : formData.reviewer_name || null,
        }),
      });

      if (response.ok) {
        setSubmitted(true);
        setFormData({ rating: 5, title: '', content: '', reviewer_name: '', isAnonymous: false });
        setTimeout(() => {
          setSubmitted(false);
          setShowForm(false);
          fetchReviews();
        }, 2000);
      }
    } catch (error) {
      console.error('Error submitting review:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const StarRating = ({ rating, interactive = false, onRate = null }) => (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map(i => (
        <button
          key={i}
          type={interactive ? 'button' : undefined}
          onClick={interactive ? () => onRate(i) : undefined}
          className={interactive ? 'cursor-pointer hover:scale-110 transition-transform' : ''}
          disabled={!interactive}
        >
          <Star 
            size={interactive ? 28 : 16} 
            fill={i <= rating ? '#f59e0b' : 'none'} 
            stroke={i <= rating ? '#f59e0b' : '#d1d5db'} 
          />
        </button>
      ))}
    </div>
  );

  const ageStats = stats[age] || { count: 0, avg_rating: 0 };
  const displayedReviews = showAll ? reviews : reviews.slice(0, 3);

  return (
    <div className="mt-12">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h2 className="heading-2 mb-2">Parent Reviews</h2>
          {ageStats.count > 0 && (
            <div className="flex items-center gap-3">
              <StarRating rating={Math.round(ageStats.avg_rating)} />
              <span className="body-medium font-semibold">{ageStats.avg_rating}</span>
              <span className="body-small" style={{ color: 'var(--text-secondary)' }}>
                ({ageStats.count} review{ageStats.count !== 1 ? 's' : ''})
              </span>
            </div>
          )}
        </div>
        <button 
          onClick={() => setShowForm(!showForm)} 
          className="btn-secondary"
        >
          {showForm ? 'Cancel' : 'Write a Review'}
        </button>
      </div>

      {/* Review Form */}
      {showForm && (
        <div className="card mb-8" style={{ 
          background: 'var(--glass-card)',
          backdropFilter: 'blur(20px)',
        }}>
          {submitted ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center bg-green-100">
                <Star size={32} className="text-green-600" fill="currentColor" />
              </div>
              <h3 className="heading-3 mb-2">Thank You!</h3>
              <p className="body-medium" style={{ color: 'var(--text-secondary)' }}>
                Your review has been submitted.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <h3 className="heading-3 mb-4">Share Your Experience</h3>
              
              <div className="mb-4">
                <label className="body-small font-medium mb-2 block">Your Rating *</label>
                <StarRating 
                  rating={formData.rating} 
                  interactive 
                  onRate={(r) => setFormData(prev => ({ ...prev, rating: r }))} 
                />
              </div>

              <div className="mb-4">
                <label className="body-small font-medium mb-2 block">Review Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Summarize your experience"
                  required
                  className="w-full px-4 py-3 rounded-lg border border-border-input focus:border-border-input-focus outline-none"
                  style={{ background: 'rgba(255, 255, 255, 0.7)' }}
                />
              </div>

              <div className="mb-4">
                <label className="body-small font-medium mb-2 block">Your Review *</label>
                <textarea
                  value={formData.content}
                  onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))}
                  placeholder="Share details about your experience with this curriculum..."
                  required
                  rows={4}
                  className="w-full px-4 py-3 rounded-lg border border-border-input focus:border-border-input-focus outline-none resize-none"
                  style={{ background: 'rgba(255, 255, 255, 0.7)' }}
                />
              </div>

              <div className="mb-6">
                <label className="flex items-center gap-2 mb-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.isAnonymous}
                    onChange={(e) => setFormData(prev => ({ ...prev, isAnonymous: e.target.checked }))}
                    className="w-4 h-4"
                  />
                  <span className="body-small">Submit anonymously</span>
                </label>
                
                {!formData.isAnonymous && (
                  <input
                    type="text"
                    value={formData.reviewer_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, reviewer_name: e.target.value }))}
                    placeholder="Your name (optional)"
                    className="w-full px-4 py-3 rounded-lg border border-border-input focus:border-border-input-focus outline-none"
                    style={{ background: 'rgba(255, 255, 255, 0.7)' }}
                  />
                )}
              </div>

              <button 
                type="submit" 
                className="btn-primary"
                disabled={submitting}
              >
                {submitting ? 'Submitting...' : 'Submit Review'}
              </button>
            </form>
          )}
        </div>
      )}

      {/* Reviews List */}
      {loading ? (
        <div className="text-center py-8">
          <p className="body-medium" style={{ color: 'var(--text-secondary)' }}>Loading reviews...</p>
        </div>
      ) : reviews.length > 0 ? (
        <div className="space-y-4">
          {displayedReviews.map(review => (
            <div key={review.id} className="card">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                    <User size={20} className="text-gray-500" />
                  </div>
                  <div>
                    <p className="body-medium font-semibold">
                      {review.reviewer_name || 'Anonymous'}
                    </p>
                    <p className="caption" style={{ color: 'var(--text-secondary)' }}>
                      {new Date(review.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </p>
                  </div>
                </div>
                <StarRating rating={review.rating} />
              </div>
              <h4 className="body-medium font-semibold mb-2">{review.title}</h4>
              <p className="body-small" style={{ color: 'var(--text-secondary)' }}>{review.content}</p>
            </div>
          ))}
          
          {reviews.length > 3 && (
            <button 
              onClick={() => setShowAll(!showAll)}
              className="w-full py-3 text-center body-medium font-medium flex items-center justify-center gap-2"
              style={{ color: 'var(--brand-primary)' }}
            >
              {showAll ? (
                <>Show Less <ChevronUp size={20} /></>
              ) : (
                <>Show All {reviews.length} Reviews <ChevronDown size={20} /></>
              )}
            </button>
          )}
        </div>
      ) : (
        <div className="card text-center py-8">
          <Star size={48} className="mx-auto mb-4 text-gray-300" />
          <p className="body-medium mb-2" style={{ color: 'var(--text-secondary)' }}>No reviews yet</p>
          <p className="body-small" style={{ color: 'var(--text-tertiary)' }}>Be the first to share your experience!</p>
        </div>
      )}
    </div>
  );
};

export default BookReviews;
